import React, { useContext, useState, useEffect } from "react";
import { auth, db } from "../firebase/firebaseConfig";
import { signOut } from "firebase/auth"; // Add this import

import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, updateDoc } from "firebase/firestore";

const AuthContext = React.createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0); // Store wallet balance
  const [transactions, setTransactions] = useState([]); // Store transactions
  const [phoneNumber, setPhoneNumber] = useState(null); // Store phone number
  const [name, setName] = useState(null); // Store name
  const [loading, setLoading] = useState(true);

  // Function to update phone number in Firestore
  const updatePhoneNumber = async (newPhoneNumber) => {
    if (!currentUser) {
      console.error("No user is logged in.");
      return;
    }

    const userDocRef = doc(db, "users", currentUser.uid);

    try {
      await updateDoc(userDocRef, {
        phoneNumber: newPhoneNumber,
      });
      setPhoneNumber(newPhoneNumber); // Update local state
      console.log("Phone number updated successfully!");
    } catch (error) {
      console.error("Error updating phone number: ", error);
    }
  };

    const logout = async () => {
      try {
        await signOut(auth);
        // Reset all user-related states
        setCurrentUser(null);
        setWalletBalance(0);
        setTransactions([]);
        setPhoneNumber(null);
        setName(null);
      } catch (error) {
        console.error("Error signing out: ", error);
      }
    };

  // Function to update name in Firestore
  const updateName = async (newName) => {
    if (!currentUser) {
      console.error("No user is logged in.");
      return;
    }

    const userDocRef = doc(db, "users", currentUser.uid);

    try {
      await updateDoc(userDocRef, {
        name: newName,
      });
      setName(newName); // Update local state
      console.log("Name updated successfully!");
    } catch (error) {
      console.error("Error updating name: ", error);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        const userDocRef = doc(db, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();

          // Set wallet balance
          if (userData.wallet) {
            setWalletBalance(userData.wallet.balance || 0);
          } else {
            setWalletBalance(0);
          }

          // Set transactions
          if (userData.wallet?.transactions) {
            setTransactions(userData.wallet.transactions);
          } else {
            setTransactions([]);
          }

          // Set phone number
          if (userData.phoneNumber) {
            setPhoneNumber(userData.phoneNumber);
          } else {
            setPhoneNumber(null); // No phone number found
          }

          // Set name
          if (userData.name) {
            setName(userData.name);
          } else {
            setName(null); // No name found
          }
        } else {
          // If user document doesn't exist, initialize default values
          setWalletBalance(0);
          setTransactions([]);
          setPhoneNumber(null);
          setName(null);
        }
      } else {
        // If no user is logged in, reset all states
        setWalletBalance(0);
        setTransactions([]);
        setPhoneNumber(null);
        setName(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        walletBalance,
        setWalletBalance,
        transactions, // Provide transactions to the app
        phoneNumber, // Provide phone number to the app
        updatePhoneNumber, // Provide function to update phone number
        name, // Provide name to the app
        updateName, // Provide function to update name
        logout
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
}
